import type {SavedView} from './types'

export const VIEWS_SORT_OPTIONS = [
  {label: 'Recently Updated', value: 'recently_updated'},
  {label: 'Newest', value: 'newest'},
  {label: 'Oldest', value: 'oldest'},
  {label: 'Least Recently Updated', value: 'least_recently_updated'},
  {label: 'Name', value: 'name'},
] as const

export type ViewsSortOption = (typeof VIEWS_SORT_OPTIONS)[number]
export type ViewsSortValue = ViewsSortOption['value']

const DEFAULT_VIEWS_SORT_OPTION: ViewsSortOption = VIEWS_SORT_OPTIONS[0]
export const DEFAULT_VIEWS_SORT: ViewsSortValue = DEFAULT_VIEWS_SORT_OPTION.value

export function isViewsSortValue(value: string | null | undefined): value is ViewsSortValue {
  return VIEWS_SORT_OPTIONS.some(option => option.value === value)
}

export function getViewsSortOption(value: ViewsSortValue): ViewsSortOption {
  return VIEWS_SORT_OPTIONS.find(option => option.value === value) ?? DEFAULT_VIEWS_SORT_OPTION
}

export function countPinnedViews(views: readonly SavedView[] = []): number {
  return views.filter(view => view.pinned).length
}

export function sortViews(
  views: readonly SavedView[],
  sort: ViewsSortValue,
  {pinnedFirst = false}: {pinnedFirst?: boolean} = {},
): SavedView[] {
  // `views` comes straight from the query cache and every sort below is in-place,
  // so this copy is what keeps us from mutating cached data. Don't remove it.
  const copy = [...views]
  switch (sort) {
    case 'newest':
      copy.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
      break
    case 'oldest':
      copy.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
      break
    case 'least_recently_updated':
      copy.sort((a, b) => Date.parse(a.updatedAt) - Date.parse(b.updatedAt))
      break
    case 'name':
      copy.sort((a, b) => a.name.localeCompare(b.name))
      break
    case 'recently_updated':
    default:
      copy.sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
      break
  }

  if (!pinnedFirst) return copy

  // `copy` is already ordered by the chosen sort and Array.sort is stable, so ordering by
  // pinned-first preserves each group's existing order.
  return copy.sort((a, b) => Number(b.pinned) - Number(a.pinned))
}
